import React from 'react';
import { useTranslation } from 'react-i18next';

interface PrivacyPolicyViewerProps {
  onClose?: () => void;
  mode?: 'modal' | 'page';
}

interface PolicyTable {
  headers: string[];
  rows: string[][];
}

/**
 * i18n runs with `returnEmptyString: false` plus a `parseMissingKeyHandler`
 * (see src/i18n/config.ts), so an absent or empty locale value resolves to the
 * literal `[MISSING: <key>]` marker instead of "". Optional policy fields must
 * therefore be checked for that marker as well as for emptiness before render.
 */
const hasValue = (value?: string): boolean =>
  typeof value === 'string' && value.trim() !== '' && !value.startsWith('[MISSING:');

export const PrivacyPolicyViewer: React.FC<PrivacyPolicyViewerProps> = ({ onClose, mode = 'modal' }) => {
  const { t } = useTranslation('privacy');

  const renderList = (items: string[], ordered?: boolean) => {
    const ListTag = ordered ? 'ol' : 'ul';
    return (
      <ListTag className={`${ordered ? 'list-decimal' : 'list-disc'} ml-6 space-y-2 mb-4`}>
        {items.map((item, idx) => (
          <li key={idx} className="text-gray-700 leading-relaxed">{item}</li>
        ))}
      </ListTag>
    );
  };

  const renderTable = (table: PolicyTable) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            {table.headers.map((header, idx) => (
              <th
                key={idx}
                className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-900 align-top"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {row.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  className="border border-gray-200 px-3 py-2 text-gray-700 align-top leading-relaxed"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSection = (section: any, index: number) => {
    return (
      <div key={index} className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
        
        {section.content && (
          <p className="mb-4 leading-relaxed text-gray-700">{section.content}</p>
        )}
        
        {section.items && renderList(section.items, section.ordered)}

        {section.table && renderTable(section.table)}

        {section.note && (
          <p className="mb-4 text-xs leading-relaxed text-gray-500">{section.note}</p>
        )}
        
        {section.subsections && section.subsections.map((subsection: any, subIdx: number) => (
          <div key={subIdx} className="ml-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{subsection.title}</h3>
            
            {subsection.content && (
              <p className="mb-3 leading-relaxed text-gray-700">{subsection.content}</p>
            )}
            
            {subsection.items && renderList(subsection.items, subsection.ordered)}

            {subsection.table && renderTable(subsection.table)}

            {subsection.note && (
              <p className="mt-2 text-xs leading-relaxed text-gray-500">{subsection.note}</p>
            )}
          </div>
        ))}
        
        {section.contact && (
          <div className="ml-4 mt-3 p-4 bg-gray-50 rounded-lg">
            {hasValue(section.contact.officer) && (
              <p className="text-gray-700"><strong>{section.contact.officer}</strong></p>
            )}
            {hasValue(section.contact.company) && (
              <p className="text-gray-700"><strong>{section.contact.company}</strong></p>
            )}
            {hasValue(section.contact.email) && (
              <p className="text-gray-700">{section.contact.email}</p>
            )}
            {hasValue(section.contact.phone) && (
              <p className="text-gray-700">{section.contact.phone}</p>
            )}
            {hasValue(section.contact.address) && (
              <p className="text-gray-700">{section.contact.address}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div className="text-gray-700">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
        <p className="text-sm text-gray-600">{t('version')}</p>
        <p className="text-sm text-gray-600">{t('lastUpdated')}</p>
      </div>
      
      {t('sections', { returnObjects: true }) && 
        (t('sections', { returnObjects: true }) as any[]).map((section, index) => 
          renderSection(section, index)
        )
      }
      
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600 italic">{t('footer')}</p>
      </div>
    </div>
  );

  if (mode === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">{t('title')}</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            {content}
          </div>
        </div>
      </div>
    );
  }

  // Page mode
  return (
    <div className="max-w-4xl mx-auto">
      {content}
    </div>
  );
};
